import {Module} from '@mamdev/cad.static-next-lib';
import Viewport from '../../ts/services/viewport';
import Tracking2 from '../../ts/services/tracking2';

/**
 * Some functionalities of "NativeImage" type
 */
export default class NativeImage extends Module {
  static attachTo(root: HTMLElement): NativeImage {
    return new NativeImage(root);
  }

  constructor(root: HTMLElement) {
    super(root);
    this.name_ = 'native-image';
  }

  /**
   * Start module
   */
  start() {
    this.initImage();
  }

  /**
   * initImage
   */
  initImage() {
    try {
      // Generate the elements needed for the background effect, if necessary
      if (this.root_.dataset.backgroundEffect === 'blur') {
        this.setBlurEffectToTheImage();
      }

      if (!this.root_.classList.contains('js-zoomable')) {
        this.setClickTrackingHandler();
      } else {
        this.setMagnifierEvent();
      }
    } catch (e) {
      this.logUnknownError(e, true);
    }
  }

  setClickTrackingHandler() {
    this.root_.querySelectorAll('a').forEach(elem => {
      elem.addEventListener('click', () => {
        const anchor = this.root_.querySelector('a');
        const componentPath = anchor?.getAttribute('data-component-path') ?? '';
        const componentName = anchor?.getAttribute('data-component-name') ?? '';
        if (componentPath) {
          Tracking2.callTealiumLink({
            businessEventType: 'userAction',
            componentPath,
            componentName,
            targetUrl: anchor?.href,
            eventType: 'mousedown',
            elementTagName: 'a'
          });
        }
      });
    });
  }

  /**
   * Setup for images that provide magnifier functionality
   */
  setMagnifierEvent() {
    try {
      this.root_.querySelectorAll('a').forEach(elem => {
        elem.addEventListener('click', evt => {
          evt.preventDefault();

          if (!Viewport.largerThan('tablet')) {
            return; // CADNPCA-7120: enable magnifier function only for desktop+ view
            // also: click event for mobile view disabled with `point-events: none`, check elements.less
          }

          const parent = this.root_.parentElement;

          if (parent?.classList.contains('article-inner-slideshow-object')) {
            return;
          }

          let imageUrl = '';
          let copyrightText = '';
          let descriptionText = '';

          const imgMagnifierHref = this.root_.getAttribute('data-magnifier-img-url');
          const anker = this.root_.querySelector('a');

          const componentPath = anker?.getAttribute('data-component-path') ?? '';
          const componentName = anker?.getAttribute('data-component-name') ?? '';

          if (imgMagnifierHref) {
            imageUrl = imgMagnifierHref;
          } else if (anker?.href) {
            imageUrl = anker?.href;
          } else {
            this.publish('sentry:log', {error: 'No image url given for magnifier', module: 'native-image'});
            return;
          }

          const crt = this.root_.querySelectorAll<HTMLElement>('figcaption .copyright');

          if (crt && crt.length > 0) {
            copyrightText = crt[0].innerText;
          } else {
            const leadCopyright = document.getElementById('lead__copyright');
            if (leadCopyright) {
              copyrightText = leadCopyright.innerText;
            }
          }

          const dt = this.root_.querySelectorAll<HTMLElement>('figcaption .description');

          if (dt && dt.length > 0) {
            descriptionText = dt[0].innerText;
          } else {
            const leadDescription = document.getElementById('lead__description');
            if (leadDescription) {
              descriptionText = leadDescription.innerText;
            }
          }

          const imgInfo = {
            pictureContent: imageUrl,
            copyright: copyrightText,
            description: descriptionText,
            trackingData: {
              componentPath,
              componentName
            }
          };

          this.publish('article:magnifier', imgInfo);
        });
      });
    } catch (e) {
      this.logUnknownError(e, true);
    }
  }

  /** get the current selected source img path to set a blur effect */
  setBlurEffectToTheImage() {
    try {
      const pictureContent = this.root_.querySelector('picture');
      const imgTag = pictureContent?.querySelector('img');

      if (pictureContent && imgTag) {
        const currentSelectedPictureSourceUrl = imgTag.src;
        const backgroundContainer = document.createElement('div');

        backgroundContainer.classList.add('background-image-container');

        const backgroundElement = document.createElement('div');
        backgroundElement.classList.add('background-image');
        backgroundElement.style.backgroundImage = 'url(' + currentSelectedPictureSourceUrl + ')';

        backgroundContainer.appendChild(backgroundElement);
        imgTag.parentNode?.insertBefore(backgroundContainer, imgTag);
      }
    } catch (e) {
      this.logUnknownError(e, true);
    }
  }
}
